import config from '../config'

export interface ImageSearchItem {
  id: number
  node_id: number
  filename: string
  original_filename?: string | null
  file_type: string
  file_size: number
  created_at: string
  thumbnail_url?: string | null
  upload_method: string
  preprocess_state?: string | null
  sub_type?: string | null
  metadata?: Record<string, any> | null
  node_path: string
  similarity: number
  model_key: string
}

export interface ClusterSummary {
  id: number
  cluster_idx: number
  label: string
  size: number
  share: number
  avg_similarity: number
  representative_file_ids: number[]
  representative_thumbnail_urls: string[]
}

export interface ScatterPoint {
  file_id: number
  cluster_id: number
  x: number
  y: number
  z: number
}

export interface ClusterScatter {
  run_id: number
  total: number
  sampled: number
  points: ScatterPoint[]
}

export interface ClusterNode {
  id: number
  node_path: string
  depth: number
  parent_id?: number | null
  is_leaf: boolean
  label?: string | null
  size: number
  visible_size: number
  share: number
  mean_distance?: number | null
  cohesion_gain?: number | null
  split_seed_ari?: number | null
  stop_reason?: string | null
  children_count: number
  outlier_count: number
  representative_file_ids: number[]
  representative_thumbnail_urls: string[]
  boundary_file_ids: number[]
  boundary_thumbnail_urls: string[]
}

export interface ClusterTree {
  run_id: number
  algorithm: string
  total_visible: number
  parent?: ClusterNode | null
  breadcrumb: ClusterNode[]
  children: ClusterNode[]
}

export interface ClusterFilePage {
  total: number
  items: ImageSearchItem[]
}

/** 평면 분포 목록의 카드 하나. 깊이는 감사용으로만 남기고 화면에는 쓰지 않는다. */
export interface FlatCluster {
  id: number
  cluster_idx: number
  node_path: string
  depth: number
  is_leaf: boolean
  label?: string | null
  visible_size: number
  share: number
  mean_distance?: number | null
  outlier_count: number
  stop_reason?: string | null
  boundary_ratio?: number | null
  /** 썸네일 URL 과 같은 순서의 파일 id — 눌러서 원본을 열 때 쓴다 */
  representative_file_ids: number[]
  representative_thumbnail_urls: string[]
  boundary_file_ids: number[]
  boundary_thumbnail_urls: string[]
  /** 위 목록과 같은 순서의 중심 유사도 (1 - 중심까지 코사인 거리) */
  representative_similarities: number[]
  boundary_similarities: number[]
}

export interface FlatClusterList {
  run_id: number
  algorithm: string
  level: 'leaf' | 'top'
  total_visible: number
  clusters: FlatCluster[]
  leaf_stability_note?: string | null
}

export interface BoundarySample {
  file_id: number
  thumbnail_url: string
  distance_to_centroid?: number | null
  cluster_id: number
  cluster_path: string
  same_cluster: boolean
}

export interface BoundaryView {
  run_id: number
  cluster_id: number
  is_leaf: boolean
  total: number
  samples: BoundarySample[]
  note?: string | null
}

export interface SplitCandidate {
  index: number
  size: number
  cohesion: number
  parent_cohesion: number
  tighter_than_parent: boolean
  mean_distance_to_parent_center: number
  representative_file_ids: number[]
  representative_thumbnail_urls: string[]
}

export interface SplitCandidateView {
  run_id: number
  cluster_id: number
  cluster_path: string
  cluster_size: number
  analyzed: number
  min_candidate_size: number
  candidates: SplitCandidate[]
  not_split_reasons: string[]
  note?: string | null
}

export interface EmbeddingStatus {
  total_images: number
  embedded: number
  pending: number
  source_missing: number
  other_failed: number
  transient_failed: number
  permanent_failed: number
  is_complete: boolean
  progress: number
}

/** 검색 결과 한 묶음. total 은 관련도 하한을 넘은 개수, truncated 면 더 있을 수 있다. */
export interface SearchResultPage {
  items: ImageSearchItem[]
  total: number
  truncated: boolean
  /** 1위 유사도 대비 하한 비율 */
  min_relative: number
  /** 이번 질의의 1위 유사도 */
  top_similarity: number
}

/** 클러스터링 1단계 — 목록 카드 하나 */
export interface ClusteringProject {
  id: number
  name: string
  description?: string | null
  created_at: string
  created_by_name?: string | null
  run_count: number
  completed_count: number
  running_count: number
  latest_run_at?: string | null
}

/** 프로젝트 안의 클러스터링 하나 */
export interface ClusteringRunItem {
  id: number
  project_id: number
  status: 'queued' | 'running' | 'completed' | 'failed'
  n_samples: number
  node_count: number
  leaf_count: number
  error?: string | null
  started_at?: string | null
  finished_at?: string | null
  source_labels: string[]
}

export interface ClusteringProjectDetail {
  id: number
  name: string
  description?: string | null
  created_at: string
  runs: ClusteringRunItem[]
}

/** 대상 선택 트리의 폴더 하나. 최하위 폴더만 고를 수 있다. */
export interface ClusteringSourceNode {
  id: number
  parent_id?: number | null
  name: string
  child_count: number
  is_leaf: boolean
  total_images: number
  embedded: number
  pending: number
  excluded: number
  selectable: boolean
  reason?: string | null
}

class EmbeddingService {
  private baseUrl = config.API_BASE_URL

  private headers(): HeadersInit {
    return {
      Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
      'Content-Type': 'application/json',
    }
  }

  private async parse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let message = `요청 실패 (${response.status})`
      try {
        const body = await response.json()
        if (body?.detail) message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail)
      } catch {}
      throw new Error(message)
    }
    return response.json() as Promise<T>
  }

  /** 자연어 검색.
   *
   * topK 는 「보여줄 개수」가 아니라 「최대 몇 개까지 훑을까」다. 유사도 하한을 넘는
   * 결과를 한 번에 받아 화면에서 페이지로 잘라 쓴다 — 페이지마다 다시 부르면 질의문
   * 임베딩(vLLM 왕복)이 페이지 수만큼 늘고 그 사이 순서가 달라질 수도 있다.
   */
  async search(
    query: string, topK: number = 500, nodeId?: number, minRelative: number = 0,
  ): Promise<SearchResultPage> {
    const response = await fetch(`${this.baseUrl}/embeddings/search`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({
        query, top_k: topK, node_id: nodeId, min_relative: minRelative,
      }),
    })
    return this.parse<SearchResultPage>(response)
  }

  /** 검색결과를 ZIP 으로 받는다.
   *
   * 파일 id 목록은 URL 에 담을 수 없어 POST 로 준비하고, 전송만 GET 으로 넘긴다
   * (대용량 ZIP 을 blob 으로 받으면 브라우저 메모리가 터진다 — downloadCluster 와
   * 같은 이유다). 헤더를 붙일 수 없으므로 access_token 쿼리로 인증한다.
   */
  async downloadSearchResults(
    fileIds: number[], filename: string, snapshot?: string,
  ): Promise<{ status: 'ok' } | { status: 'processing'; message?: string; pollingUrl?: string; snapshot?: string; fileCount?: number }> {
    const name = filename.trim()
    const probe = await fetch(`${this.baseUrl}/embeddings/search/download`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({
        file_ids: fileIds, filename: name || undefined,
        prepare_only: true, snapshot: snapshot || undefined,
      }),
    })
    if (probe.status === 202) {
      const data = await probe.json().catch(() => ({} as any))
      return {
        status: 'processing',
        message: data?.message || '패키징 중입니다.',
        pollingUrl: data?.polling_url,
        snapshot: data?.snapshot,
        fileCount: data?.file_count,
      }
    }
    if (!probe.ok) {
      let detail = (await probe.text()).trim()
      try {
        const parsed = JSON.parse(detail)
        if (typeof parsed?.detail === 'string') detail = parsed.detail
      } catch { /* JSON 이 아니면 본문 그대로 */ }
      throw new Error(detail || `다운로드 실패(${probe.status})`)
    }
    const ready = await probe.json().catch(() => ({} as any))
    if (!ready?.snapshot) throw new Error('다운로드 준비 정보를 받지 못했습니다')
    const token = localStorage.getItem('access_token') || ''
    const params = new URLSearchParams({ snapshot: String(ready.snapshot) })
    if (name) params.set('filename', name)
    if (token) params.set('access_token', token)
    const a = document.createElement('a')
    a.href = `${this.baseUrl}/embeddings/search/download?${params.toString()}`
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    return { status: 'ok' }
  }

  async status(): Promise<EmbeddingStatus> {
    const response = await fetch(`${this.baseUrl}/embeddings/status`, { headers: this.headers() })
    return this.parse<EmbeddingStatus>(response)
  }

  /** 계층 분포 트리. nodeId 를 주면 그 노드로 내려간다(0/미지정이면 루트). */
  async clusterTree(runId: number, nodeId: number = 0): Promise<ClusterTree> {
    const response = await fetch(
      `${this.baseUrl}/embeddings/clusters/tree?run_id=${runId}&node_id=${nodeId}`,
      { headers: this.headers() },
    )
    return this.parse<ClusterTree>(response)
  }

  /** 평면 분포 목록. level=leaf 는 최종 묶음 전부, top 은 상위 묶음.
   *  reps 는 묶음마다 받을 대표/경계 썸네일 수 (화면이 보여주는 수와 맞춘다). */
  async clusterFlat(
    runId: number, level: 'leaf' | 'top' = 'leaf', reps: number = 5,
  ): Promise<FlatClusterList> {
    const response = await fetch(
      `${this.baseUrl}/embeddings/clusters/flat?run_id=${runId}&level=${level}&reps=${reps}`,
      { headers: this.headers() },
    )
    return this.parse<FlatClusterList>(response)
  }

  /** 경계 표본 + 각 사진이 실제로 속한 최종 묶음. */
  async clusterBoundary(clusterId: number, runId: number, limit: number = 24): Promise<BoundaryView> {
    const response = await fetch(
      `${this.baseUrl}/embeddings/clusters/${clusterId}/boundary?run_id=${runId}&limit=${limit}`,
      { headers: this.headers() },
    )
    return this.parse<BoundaryView>(response)
  }

  /** 경계 표본이 별도 덩어리를 이루는지 — 놓친 하위 묶음 후보. */
  async clusterSplitCandidates(clusterId: number, runId: number): Promise<SplitCandidateView> {
    const response = await fetch(
      `${this.baseUrl}/embeddings/clusters/${clusterId}/split-candidates?run_id=${runId}`,
      { headers: this.headers() },
    )
    return this.parse<SplitCandidateView>(response)
  }

  async clusterScatter(limit: number, runId: number): Promise<ClusterScatter> {
    const response = await fetch(
      `${this.baseUrl}/embeddings/clusters/scatter?limit=${limit}&run_id=${runId}`,
      { headers: this.headers() },
    )
    return this.parse<ClusterScatter>(response)
  }

  async clusterFiles(
    clusterId: number, skip: number, limit: number, runId: number,
  ): Promise<ClusterFilePage> {
    const response = await fetch(
      `${this.baseUrl}/embeddings/clusters/${clusterId}/files?skip=${skip}&limit=${limit}&run_id=${runId}`,
      { headers: this.headers() },
    )
    return this.parse<ClusterFilePage>(response)
  }

  async updateClusterLabel(clusterId: number, label: string): Promise<{ id: number; label: string }> {
    const response = await fetch(`${this.baseUrl}/embeddings/clusters/${clusterId}/label`, {
      method: 'PATCH',
      headers: this.headers(),
      body: JSON.stringify({ label }),
    })
    return this.parse<{ id: number; label: string }>(response)
  }

  /** 클러스터 이미지를 ZIP 으로 받는다.
   *
   * 대용량 ZIP 을 fetch().blob() 에 올리면 브라우저 메모리가 터진다. prepare_only 로
   * 준비 여부만 확인하고, 준비되면 <a href> 로 브라우저에 맡긴다. 헤더를 붙일 수
   * 없으므로 access_token 쿼리로 인증한다 (원천데이터·학습 데이터셋과 동일).
   */
  async downloadCluster(
    clusterId: number, runId: number, filename: string, snapshot?: string,
  ): Promise<{ status: 'ok' } | { status: 'processing'; message?: string; pollingUrl?: string; snapshot?: string; fileCount?: number }> {
    const token = localStorage.getItem('access_token') || ''
    const name = filename.trim()
    const probeParams = new URLSearchParams({ run_id: String(runId), prepare_only: 'true' })
    if (name) probeParams.set('filename', name)
    if (snapshot) probeParams.set('snapshot', snapshot)

    const probe = await fetch(
      `${this.baseUrl}/embeddings/clusters/${clusterId}/download?${probeParams.toString()}`,
      { headers: this.headers() },
    )
    if (probe.status === 202) {
      const data = await probe.json().catch(() => ({} as any))
      return {
        status: 'processing',
        message: data?.message || '패키징 중입니다.',
        pollingUrl: data?.polling_url,
        snapshot: data?.snapshot,
        fileCount: data?.file_count,
      }
    }
    if (!probe.ok) {
      let detail = (await probe.text()).trim()
      try {
        const parsed = JSON.parse(detail)
        if (typeof parsed?.detail === 'string') detail = parsed.detail
      } catch { /* JSON 이 아니면 본문 그대로 */ }
      throw new Error(detail || `다운로드 실패(${probe.status})`)
    }

    const ready = await probe.json().catch(() => ({} as any))
    const params = new URLSearchParams({ run_id: String(runId) })
    if (name) params.set('filename', name)
    if (ready?.snapshot) params.set('snapshot', ready.snapshot)
    if (token) params.set('access_token', token)
    const a = document.createElement('a')
    a.href = `${this.baseUrl}/embeddings/clusters/${clusterId}/download?${params.toString()}`
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    return { status: 'ok' }
  }

  /** 패키징 진행 상태 폴링 — 202 응답의 polling_url 사용.
   *  progress 는 0~99 (완료는 status='ready'). 서버가 총량을 모르는 극초반에는 없다. */
  async checkDownloadStatus(
    pollingUrl: string,
  ): Promise<{ status: string; progress?: number; message?: string; packed_bytes?: number }> {
    const path = pollingUrl.startsWith('/') ? pollingUrl : `/${pollingUrl}`
    const response = await fetch(`${this.baseUrl}${path}`, { headers: this.headers() })
    if (!response.ok) return { status: 'processing' }
    return response.json()
  }

  // --- 클러스터링 프로젝트 -------------------------------------------------

  async clusteringProjects(): Promise<ClusteringProject[]> {
    const response = await fetch(`${this.baseUrl}/embeddings/clustering-projects`, {
      headers: this.headers(),
    })
    return this.parse<ClusteringProject[]>(response)
  }

  async createClusteringProject(name: string, description?: string): Promise<ClusteringProject> {
    const response = await fetch(`${this.baseUrl}/embeddings/clustering-projects`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ name, description: description || null }),
    })
    return this.parse<ClusteringProject>(response)
  }

  async clusteringProject(projectId: number): Promise<ClusteringProjectDetail> {
    const response = await fetch(
      `${this.baseUrl}/embeddings/clustering-projects/${projectId}`,
      { headers: this.headers() },
    )
    return this.parse<ClusteringProjectDetail>(response)
  }

  async deleteClusteringProject(projectId: number): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}/embeddings/clustering-projects/${projectId}`,
      { method: 'DELETE', headers: this.headers() },
    )
    await this.parse<{ id: number }>(response)
  }

  /** 대상 선택 트리. nodeId 를 주지 않으면 루트 폴더들. */
  async clusteringSources(nodeId: number = 0): Promise<ClusteringSourceNode[]> {
    const response = await fetch(
      `${this.baseUrl}/embeddings/clustering-sources?node_id=${nodeId}`,
      { headers: this.headers() },
    )
    return this.parse<ClusteringSourceNode[]>(response)
  }

  /** 선택한 폴더로 클러스터링을 만든다. 실제 계산은 Airflow 가 집어간다. */
  async createClusteringRun(projectId: number, nodeIds: number[]): Promise<ClusteringRunItem> {
    const response = await fetch(
      `${this.baseUrl}/embeddings/clustering-projects/${projectId}/runs`,
      { method: 'POST', headers: this.headers(), body: JSON.stringify({ node_ids: nodeIds }) },
    )
    return this.parse<ClusteringRunItem>(response)
  }

  async deleteClusteringRun(runId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/embeddings/clustering-runs/${runId}`, {
      method: 'DELETE', headers: this.headers(),
    })
    await this.parse<{ id: number }>(response)
  }

  mediaUrl(path?: string | null): string {
    if (!path) return ''
    if (/^https?:\/\//.test(path)) return path
    return `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`
  }

  imageUrl(fileId: number): string {
    return `${this.baseUrl}/files/${fileId}/image`
  }
}

const embeddingService = new EmbeddingService()
export default embeddingService
